/**
 * Le piano du studio, branché sur l'ordonnanceur d'audition.
 *
 * # Pourquoi il existe
 *
 * L'extrait d'une fiche de catalogue passait par `GmSoundfontSynth`, la banque
 * General MIDI qui sert à auditionner un fichier importé — trois échantillons
 * pivots par programme, transposés par `Tone.Sampler` sur toute la tessiture.
 * C'est le bon compromis quand on ne sait pas ce qu'on va entendre : un fichier
 * importé peut porter seize instruments. Ce n'est pas le bon quand on le sait :
 * le catalogue est du piano solo, et la fiche donnait donc un piano étiré
 * depuis trois notes là où le piano-roll, deux clics plus loin, joue le
 * Salamander échantillonné toutes les tierces mineures. Deux sons pour le même
 * morceau, dont un moins beau, sur l'écran qui sert justement à décider.
 *
 * # Ce que la classe fait, et ne fait pas
 *
 * Elle adapte, elle ne rejoue rien : `SalamanderPianoSynth` porte déjà les
 * couches de vélocité, la chaîne d'effets et la garde de chargement.
 * `gmProgram` et `isDrum` sont reçus et ignorés — un banc mono-instrument n'a
 * pas de programme à choisir —, et `ensureProgram` / `ensureDrums` se replient
 * sur `whenReady()`, seul chargement qu'il y ait à attendre.
 *
 * # Un singleton, comme son voisin GM
 *
 * Les échantillons pèsent ~1,3 Mo. Une instance par fiche visitée les
 * retéléchargerait à chaque navigation dans la bibliothèque, sur une page où
 * l'on passe justement d'un morceau au suivant. Le premier clic sur « écouter »
 * paie, les autres non — et c'est bien un clic qui paie, jamais un montage :
 * la plupart des visiteurs d'une fiche n'écoutent pas.
 *
 * L'extrait d'accord de la scène d'harmonie (`use-chord-audition`) partage ce
 * même banc. Il reste distinct des synthés du studio, qui appartiennent aux
 * lanes : couper l'extrait ne doit jamais couper les notes du joueur, et c'est
 * `releaseAllNotes` d'une instance qui garantit cette frontière.
 */

import type { AuditionSynth } from "@/lib/midi/midi-audition-player";
import { SalamanderPianoSynth } from "./synth";

class PianoAuditionSynth implements AuditionSynth {
  private readonly piano: SalamanderPianoSynth;

  constructor(ctx: AudioContext) {
    this.piano = new SalamanderPianoSynth(ctx);
  }

  ensureProgram(): Promise<void> {
    return this.piano.whenReady();
  }

  ensureDrums(): Promise<void> {
    return this.piano.whenReady();
  }

  noteOn(midi: number, velocity: number, at: number | undefined): void {
    this.piano.noteOn(midi, velocity, at);
  }

  noteOff(midi: number, at: number | undefined): void {
    this.piano.noteOff(midi, at);
  }

  releaseAllNotes(): void {
    this.piano.releaseAllNotes();
  }
}

let instance: PianoAuditionSynth | null = null;

/**
 * Le banc de piano partagé par toutes les fiches. Construit au premier appel,
 * qui déclenche le téléchargement des échantillons.
 */
export function getPianoAuditionSynth(ctx: AudioContext): AuditionSynth {
  if (instance === null) {
    instance = new PianoAuditionSynth(ctx);
  }

  return instance;
}

/**
 * Test-only : oublie le singleton pour que l'appel suivant en rebâtisse un.
 * Jamais depuis le code de production.
 *
 * @internal
 */
export function __resetPianoAuditionSynthForTests(): void {
  instance = null;
}
